/**
 * src/shell/portal/components/NetflixDiscoveryCanvas.tsx
 *
 * Minimalist Netflix-Style Discovery Canvas for herx.org.
 * Features:
 * - Ambient Hero Live Stream preview banner
 * - Horizontal media shelves: Live Now, Garage Drops, Trending Channels, Hall of Fame
 * - 1-Click action triggers to watch streams, enter channels, or buy drops
 */

import React, { useState, useEffect } from 'react';
import { MediaShelf, ShelfItem } from './MediaShelf';
import { VideoPreviewCard } from './VideoPreviewCard';
import { MarketplaceProductCard } from './MarketplaceProductCard';
import { GarageProductCard } from './shop/GarageProductCard.js';
import { ContextualAdBanner } from './ContextualAdBanner';
import { Radio, ShoppingBag, Flame, Award, Play, ArrowRight, ShieldCheck, PlusCircle } from 'lucide-react';
import { GarageProduct } from '../../../plugins/commerce/GarageShopDrawer';
import { INITIAL_GARAGE_DROPS } from '../../../plugins/commerce/garage-shop-contracts';
import type { MediaFeedItem } from '../../../domain/media/media-feed-contracts.js';
import { UniversalMediaCard } from './media/UniversalMediaCard.js';
import { UniversalMediaComposerModal } from './media/UniversalMediaComposerModal.js';
import { ScrollableCardRow } from './ScrollableCardRow';

export interface ShelfSectionConfig {
  id: 'live_broadcasts' | 'art_gallery_drops' | 'channels' | 'arcade_hall_of_fame';
  title: string;
  isCollapsed: boolean;
  isPinnedToTop: boolean;
  orderIndex: number;
}

const DEFAULT_SHELVES: ShelfSectionConfig[] = [
  { id: 'live_broadcasts', title: 'Live Now in Channels', isCollapsed: false, isPinnedToTop: false, orderIndex: 0 },
  { id: 'art_gallery_drops', title: 'Creator Garage Shops & Drops', isCollapsed: false, isPinnedToTop: false, orderIndex: 1 },
  { id: 'channels', title: 'Trending Channels & Pinned Pulses', isCollapsed: false, isPinnedToTop: false, orderIndex: 2 },
  { id: 'arcade_hall_of_fame', title: 'Hall of Fame & Arcade Champions', isCollapsed: false, isPinnedToTop: false, orderIndex: 3 },
];

const SHELF_STORAGE_KEY = 'herx_shelf_layout_v3';

export interface NetflixDiscoveryCanvasProps {
  onWatchLiveHero: () => void;
  onEnterChannel: (channelId: string) => void;
  onOpenGarage: () => void;
  onSelectProduct: (product: GarageProduct) => void;
}



export const NetflixDiscoveryCanvas: React.FC<NetflixDiscoveryCanvasProps> = ({
  onWatchLiveHero,
  onEnterChannel,
  onOpenGarage,
  onSelectProduct,
}) => {
  const [shelves, setShelves] = useState<ShelfSectionConfig[]>(() => {
    try {
      if (typeof window !== 'undefined') {
        const raw = window.localStorage.getItem(SHELF_STORAGE_KEY);
        if (raw) {
          const parsed = JSON.parse(raw);
          const existingIds = new Set(parsed.map((s: any) => s.id));
          const missing = DEFAULT_SHELVES.filter((d) => !existingIds.has(d.id));
          if (missing.length > 0) {
            return [...parsed, ...missing];
          }
          return parsed;
        }
      }
    } catch {}
    return DEFAULT_SHELVES;
  });

  const saveShelves = (updated: ShelfSectionConfig[]) => {
    setShelves(updated);
    try {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(SHELF_STORAGE_KEY, JSON.stringify(updated));
      }
    } catch {}
  };

  const handleToggleCollapse = (id: string) => {
    const updated = shelves.map((s) => (s.id === id ? { ...s, isCollapsed: !s.isCollapsed } : s));
    saveShelves(updated);
  };

  const handleTogglePin = (id: string) => {
    const updated = shelves.map((s) => (s.id === id ? { ...s, isPinnedToTop: !s.isPinnedToTop } : s));
    saveShelves(updated);
  };

  const handleMoveShelf = (id: string, direction: 'UP' | 'DOWN') => {
    const sorted = [...shelves].sort((a, b) => {
      if (a.isPinnedToTop !== b.isPinnedToTop) return a.isPinnedToTop ? -1 : 1;
      return a.orderIndex - b.orderIndex;
    });

    const index = sorted.findIndex((s) => s.id === id);
    if (index < 0) return;
    const targetIndex = direction === 'UP' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= sorted.length) return;

    // Swap order indices
    const current = sorted[index];
    const target = sorted[targetIndex];
    const tempOrder = current.orderIndex;
    current.orderIndex = target.orderIndex;
    target.orderIndex = tempOrder;

    saveShelves([...sorted]);
  };

  // ─── Sovereign Media Streams (YouTube, Vimeo, Collaborative Whiteboard) ───
  const [isComposerOpen, setIsComposerOpen] = useState(false);
  const [selectedMediaItem, setSelectedMediaItem] = useState<MediaFeedItem | null>(null);
  const [mediaStreams, setMediaStreams] = useState<MediaFeedItem[]>([
    {
      id: 'stream_build_42',
      creatorDid: 'did:mesh:node_runner',
      creatorHandle: '@runner.herx',
      createdAt: Date.now() - 35 * 60 * 1000,
      type: 'video_embed',
      sessionState: 'live',
      title: 'Decentralized Mesh Build #42 (ESP32 Relay Live)',
      description: 'Live hardware assembly of the ESP32-S3 battery-backed relay bridge, local WebRTC ICE fan-out benchmarking, and peer data muling demonstration.',
      thumbnailUrl: 'https://img.youtube.com/vi/jfKfPfyJRdk/maxresdefault.jpg',
      tags: ['depin', 'esp32', 'webrtc'],
      payload: {
        video: {
          provider: 'youtube',
          platformId: 'jfKfPfyJRdk',
          embedUrl: 'https://www.youtube-nocookie.com/embed/jfKfPfyJRdk',
          isLiveStream: true,
        },
      },
      stats: { views: 1240, likes: 88, commentCount: 14 },
      context: { channelId: 'chan_node_runners', channelName: '#node-runners' },
    },
    {
      id: 'stream_screencast_party',
      creatorDid: 'did:mesh:hadmin',
      creatorHandle: '@hadmin.herx',
      createdAt: Date.now() - 18 * 60 * 1000,
      type: 'screen_share',
      sessionState: 'live',
      title: 'Dev Workshop: Real-Time WebRTC P2P Mesh Debugging',
      description: 'Host desktop live feed benchmarking coturn STUN/TURN fallback and symmetric NAT traversal.',
      thumbnailUrl: 'https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?auto=format&fit=crop&w=1200&q=80',
      tags: ['screencast', 'watch_party', 'webrtc', 'p2p'],
      payload: {
        screenShare: {
          roomId: 'room_dev_mesh_debugging',
          roomUri: 'hts://mesh/screencast/room_dev_mesh_debugging?ttl=14400',
          hostDid: 'did:mesh:hadmin',
          hostHandle: '@hadmin.herx',
          maxViewers: 3,
          activeViewerCount: 2,
          queuedViewerCount: 0,
          expiresAt: Date.now() + 3 * 3600 * 1000 + 42 * 60 * 1000,
          hasAudio: true,
          resolution: '720p30',
          targetBitrateKbps: 1800,
          isEncryptedP2P: true,
          tier: 'free',
        },
      },
      stats: { views: 84, likes: 16, commentCount: 3 },
      context: { channelId: 'chan_node_runners', channelName: '#node-runners' },
    },
    {
      id: 'stream_watch_party',
      creatorDid: 'did:mesh:showcase',
      creatorHandle: '@director.herx',
      createdAt: Date.now() - 4 * 60 * 60 * 1000,
      type: 'video_embed',
      sessionState: 'prerecorded',
      title: 'Indie Cyberpunk Short Film — Watch Party Replay',
      description: 'Full recording of the community premiere with director audio commentary and live Q&A.',
      thumbnailUrl: 'https://img.youtube.com/vi/dQw4w9WgXcQ/maxresdefault.jpg',
      tags: ['film', 'cyberpunk', 'replay'],
      payload: {
        video: {
          provider: 'youtube',
          platformId: 'dQw4w9WgXcQ',
          embedUrl: 'https://www.youtube-nocookie.com/embed/dQw4w9WgXcQ',
          isLiveStream: false,
        },
      },
      stats: { views: 480, likes: 42, commentCount: 9 },
      context: { channelId: 'chan_showcase', channelName: '#showcase' },
    },
    {
      id: 'stream_whiteboard',
      creatorDid: 'did:mesh:architect',
      creatorHandle: '@architect.herx',
      createdAt: Date.now() - 10 * 60 * 1000,
      type: 'whiteboard',
      sessionState: 'live',
      title: 'Interactive Mesh Architecture & Relay Topology',
      description: 'Collaborative live drawing session mapping out DTN store-and-forward routes.',
      tags: ['whiteboard', 'architecture', 'p2p'],
      payload: {
        whiteboard: {
          roomUri: 'hts://mesh/whiteboard/relay-topology',
          canCollaborate: true,
          strokeCount: 142,
        },
      },
      stats: { views: 310, likes: 27, commentCount: 5 },
      context: { channelId: 'chan_ai_builders', channelName: '#ai-builders' },
    },
    {
      id: 'stream_vimeo',
      creatorDid: 'did:mesh:creator_advocate',
      creatorHandle: '@monetize.herx',
      createdAt: Date.now() - 12 * 60 * 60 * 1000,
      type: 'video_embed',
      sessionState: 'prerecorded',
      title: 'Direct Creator Monetization Without Surveillance Ads',
      description: 'Session replay discussing WebRTC fanout and zero-storage platform economics.',
      thumbnailUrl: 'https://storage.googleapis.com/gtv-videos-bucket/sample/images/ForBiggerEscapes.jpg',
      tags: ['monetization', 'vimeo', 'sovereign'],
      payload: {
        video: {
          provider: 'vimeo',
          platformId: '76979871',
          embedUrl: 'https://player.vimeo.com/video/76979871',
          isLiveStream: false,
        },
      },
      stats: { views: 890, likes: 74, commentCount: 11 },
      context: { channelId: 'chan_kl_hangout', channelName: '#kl-hangout' },
    },
  ]);

  // Shelf 1: Live Now
  const liveNowItems: ShelfItem[] = [
    {
      id: 'stream_build_42',
      title: 'Decentralized Mesh Build #42',
      subtitle: 'ESP32-S3 battery bridge assembly & WebRTC fanout',
      badgeText: '🔴 1,240 Watching',
      badgeColor: '#ef4444',
      emojiIcon: '🛠️',
      extraMeta: 'in #node-runners',
      onClick: () => onEnterChannel('chan_node_runners'),
    },
    {
      id: 'stream_watch_party',
      title: 'Indie Cyberpunk Short Film Watch Party',
      subtitle: 'P2P encrypted stream with director live commentary',
      badgeText: '🔴 480 Watching',
      badgeColor: '#ef4444',
      emojiIcon: '🎬',
      extraMeta: 'in #showcase',
      onClick: () => onEnterChannel('chan_showcase'),
    },
    {
      id: 'stream_game_dev',
      title: 'Retro Shader Physics in WebGPU',
      subtitle: 'Building procedural voxel explosions live in browser',
      badgeText: '🔴 310 Watching',
      badgeColor: '#ef4444',
      emojiIcon: '🎮',
      extraMeta: 'in #ai-builders',
      onClick: () => onEnterChannel('chan_ai_builders'),
    },
    {
      id: 'stream_sea_chat',
      title: 'KL & Singapore Creator Roundtable',
      subtitle: 'Direct creator monetization without surveillance ads',
      badgeText: '🔴 890 Watching',
      badgeColor: '#ef4444',
      emojiIcon: '☕',
      extraMeta: 'in #kl-hangout',
      onClick: () => onEnterChannel('chan_kl_hangout'),
    },
  ];

  // Shelf 2: Creator Garage Drops
  const garageDropItems: ShelfItem[] = [
    {
      id: 'drop_synth',
      title: 'Analog Lo-Fi Synth Sample Pack',
      subtitle: '120 royalty-free reel-to-reel stem loops',
      badgeText: '$25 USD',
      badgeColor: '#10b981',
      emojiIcon: '🎹',
      extraMeta: '✓ Escrow Protected',
      onClick: onOpenGarage,
    },
    {
      id: 'drop_mech',
      title: 'ESP32-S3 Snap-Fit Bridge Enclosure',
      subtitle: '3D printed custom enclosure with SMA antenna cutout',
      badgeText: '15 TOKENS',
      badgeColor: '#10b981',
      emojiIcon: '📦',
      extraMeta: '8 Left in stock',
      onClick: onOpenGarage,
    },
    {
      id: 'drop_consult',
      title: '1-on-1 DePIN Architecture Consult',
      subtitle: 'Private technical deep-dive with core contributor',
      badgeText: '50 TOKENS/hr',
      badgeColor: '#10b981',
      emojiIcon: '⚡',
      extraMeta: '2 Slots available this week',
      onClick: onOpenGarage,
    },
    {
      id: 'drop_coffee',
      title: 'Artisan Micro-Lot Roasted Beans (250g)',
      subtitle: 'Direct-trade Ethiopian Yirgacheffe roasted in Bangsar',
      badgeText: '$18 USD',
      badgeColor: '#10b981',
      emojiIcon: '☕',
      extraMeta: '✓ Ships nationwide',
      onClick: onOpenGarage,
    },
  ];

  // Shelf 3: Trending Channels
  const trendingChannelsItems: ShelfItem[] = [
    {
      id: 'chan_sea',
      title: '#sea-creators',
      subtitle: 'Southeast Asia creator circle & direct revenue sharing',
      badgeText: '5.1k Members',
      badgeColor: '#38bdf8',
      emojiIcon: '🎨',
      extraMeta: '100% Organic Reach',
      onClick: () => onEnterChannel('chan_sea'),
    },
    {
      id: 'chan_node_runners',
      title: '#node-runners',
      subtitle: 'DePIN node operators, relays, and hardware bridges',
      badgeText: '2.9k Members',
      badgeColor: '#38bdf8',
      emojiIcon: '⚡',
      extraMeta: '100% Organic Reach',
      onClick: () => onEnterChannel('chan_node_runners'),
    },
    {
      id: 'chan_ai_builders',
      title: '#ai-builders',
      subtitle: 'Local WebGPU Llama-3.2, Whisper, and inference workers',
      badgeText: '3.8k Members',
      badgeColor: '#38bdf8',
      emojiIcon: '🤖',
      extraMeta: '100% Organic Reach',
      onClick: () => onEnterChannel('chan_ai_builders'),
    },
    {
      id: 'chan_kl_hangout',
      title: '#kl-hangout',
      subtitle: 'Meetups, coffee chats, and real-world micro-tasks',
      badgeText: '1.6k Members',
      badgeColor: '#38bdf8',
      emojiIcon: '📍',
      extraMeta: 'Geo-scoped community',
      onClick: () => onEnterChannel('chan_kl_hangout'),
    },
  ];

  // Shelf 4: Hall of Fame
  const hallOfFameItems: ShelfItem[] = [
    {
      id: 'top_node_1',
      title: 'NodeX-SG (Singapore Gateway)',
      subtitle: '99.99% Uptime &bull; Gold Edge Ring Champion',
      badgeText: '4.8 TB Relayed',
      badgeColor: '#f59e0b',
      emojiIcon: '🛡️',
      extraMeta: 'Top Bandwidth Relay',
    },
    {
      id: 'top_node_2',
      title: 'ApexRelay-KL (Bangsar Node)',
      subtitle: '99.95% Uptime &bull; 100 Staked Bonds',
      badgeText: '3.2 TB Relayed',
      badgeColor: '#f59e0b',
      emojiIcon: '⚡',
      extraMeta: 'Top Regional Relay',
    },
    {
      id: 'top_arcade_1',
      title: 'PixelKnight',
      subtitle: 'Sovereign Arcade Champion &bull; Score: 142,500',
      badgeText: '1st Place',
      badgeColor: '#a855f7',
      emojiIcon: '🏆',
      extraMeta: 'Won 500 Token Bounty',
    },
    {
      id: 'top_curator_1',
      title: 'Nadia Chen (@nadiachen.herx)',
      subtitle: 'Sovereign Score: 245/300 &bull; 100% Endorsement Accuracy',
      badgeText: 'Legend Curator',
      badgeColor: '#38bdf8',
      emojiIcon: '💎',
      extraMeta: '80/20 Revenue Boost Active',
    },
  ];

  return (
    <div
      style={{
        flex: 1,
        overflowY: 'auto',
        padding: '16px 20px',
        display: 'flex',
        flexDirection: 'column',
        gap: 20,
        maxWidth: 1200,
        margin: '0 auto',
        width: '100%',
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      }}
    >
      {/* ── Featured Hero Stream Banner ─────────────────────────────────── */}
      <section
        style={{
          position: 'relative',
          borderRadius: 14,
          overflow: 'hidden',
          background:
            'linear-gradient(90deg, rgba(8, 12, 22, 0.95) 0%, rgba(8, 12, 22, 0.85) 55%, rgba(8, 12, 22, 0.5) 100%), url("https://storage.googleapis.com/gtv-videos-bucket/sample/images/TearsOfSteel.jpg") center/cover no-repeat',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          boxShadow: '0 12px 36px rgba(0,0,0,0.5)',
          padding: '24px 28px',
        }}
      >
        <div style={{ maxWidth: 640 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <span
              style={{
                background: 'rgba(239, 68, 68, 0.15)',
                border: '1px solid rgba(239, 68, 68, 0.35)',
                color: '#ef4444',
                padding: '2px 8px',
                borderRadius: 4,
                fontSize: 10,
                fontWeight: 800,
                fontFamily: 'monospace',
                letterSpacing: '0.05em',
                display: 'flex',
                alignItems: 'center',
                gap: 5,
              }}
            >
              <Radio size={11} /> LIVE HERO // SFU MESH
            </span>
            <span style={{ fontSize: 11, color: '#38bdf8', fontWeight: 600, fontFamily: 'monospace' }}>
              1,240 watching in #node-runners
            </span>
          </div>

          <h1
            style={{
              fontSize: 'clamp(20px, 2.5vw, 28px)',
              fontWeight: 900,
              letterSpacing: '-0.02em',
              lineHeight: 1.25,
              margin: '0 0 8px 0',
              color: '#ffffff',
            }}
          >
            Decentralized Mesh Infrastructure Build #42
          </h1>

          <p
            style={{
              fontSize: 13,
              color: '#94a3b8',
              lineHeight: 1.5,
              margin: '0 0 16px 0',
            }}
          >
            Live hardware assembly of the ESP32-S3 battery-backed relay bridge, local WebRTC ICE fan-out benchmarking, and peer data muling demonstration.
          </p>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={onWatchLiveHero}
              style={{
                background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                border: '1px solid rgba(56, 189, 248, 0.3)',
                borderRadius: 8,
                padding: '8px 18px',
                color: '#ffffff',
                fontWeight: 700,
                fontSize: 13,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <Play size={14} fill="#ffffff" /> Watch Live
            </button>

            <button
              type="button"
              onClick={() => onEnterChannel('chan_node_runners')}
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 8,
                padding: '8px 16px',
                color: '#f8fafc',
                fontWeight: 600,
                fontSize: 13,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              Enter Channel <ArrowRight size={14} />
            </button>

            <button
              type="button"
              onClick={onOpenGarage}
              style={{
                background: 'rgba(16, 185, 129, 0.12)',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                borderRadius: 8,
                padding: '8px 16px',
                color: '#34d399',
                fontWeight: 600,
                fontSize: 13,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <ShoppingBag size={14} /> Shop Drops (3)
            </button>
          </div>
        </div>
      </section>

      {/* ── Dynamic Reorderable & Collapsible Shelves ──────────────────────── */}
      {[...shelves]
        .sort((a, b) => {
          if (a.isPinnedToTop !== b.isPinnedToTop) return a.isPinnedToTop ? -1 : 1;
          return a.orderIndex - b.orderIndex;
        })
        .map((config) => {
          if (config.id === 'live_broadcasts') {
            return (
              <div key="live_broadcasts" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 4px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 2, marginRight: 2 }}>
                      <span style={{ color: '#64748b', fontSize: 13, cursor: 'grab' }} title="Drag / Reorder Shelf">
                        ⠿
                      </span>
                      <button
                        type="button"
                        onClick={() => handleMoveShelf('live_broadcasts', 'UP')}
                        title="Move Shelf Up"
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: '#94a3b8',
                          cursor: 'pointer',
                          fontSize: 11,
                          padding: '2px 4px',
                        }}
                      >
                        ▲
                      </button>
                      <button
                        type="button"
                        onClick={() => handleMoveShelf('live_broadcasts', 'DOWN')}
                        title="Move Shelf Down"
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: '#94a3b8',
                          cursor: 'pointer',
                          fontSize: 11,
                          padding: '2px 4px',
                        }}
                      >
                        ▼
                      </button>
                    </div>
                    <span style={{ fontSize: 18 }}>🔴</span>
                    <span style={{ fontWeight: 800, fontSize: 16, color: '#f8fafc', letterSpacing: 0.2 }}>
                      {config.title}
                    </span>
                    {config.isPinnedToTop && (
                      <span
                        style={{
                          fontSize: 10,
                          color: '#38bdf8',
                          background: 'rgba(56, 189, 248, 0.15)',
                          padding: '2px 6px',
                          borderRadius: 4,
                          fontWeight: 700,
                        }}
                      >
                        PINNED
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <button
                      type="button"
                      onClick={() => handleTogglePin('live_broadcasts')}
                      style={{
                        background: config.isPinnedToTop ? 'rgba(56, 189, 248, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                        border: 'none',
                        borderRadius: 6,
                        padding: '4px 8px',
                        color: config.isPinnedToTop ? '#38bdf8' : '#94a3b8',
                        fontSize: 11,
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                      }}
                    >
                      📌 {config.isPinnedToTop ? 'Pinned' : 'Pin'}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleToggleCollapse('live_broadcasts')}
                      style={{
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: 'none',
                        borderRadius: 6,
                        padding: '4px 8px',
                        color: '#94a3b8',
                        fontSize: 11,
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      {config.isCollapsed ? '▸ Expand' : '▾ Collapse'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsComposerOpen(true)}
                      style={{
                        background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                        border: 'none',
                        borderRadius: 6,
                        padding: '4px 10px',
                        color: '#ffffff',
                        fontSize: 11,
                        fontWeight: 700,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        boxShadow: '0 2px 8px rgba(2, 132, 199, 0.4)',
                      }}
                    >
                      <PlusCircle size={13} /> Broadcast / Stream
                    </button>
                    {!config.isCollapsed && (
                      <button
                        type="button"
                        onClick={onWatchLiveHero}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: '#38bdf8',
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                        }}
                      >
                        Explore <ArrowRight size={14} />
                      </button>
                    )}
                  </div>
                </div>

                {!config.isCollapsed && (
                  <ScrollableCardRow gap={14}>
                    {mediaStreams.map((stream) => (
                      <div
                        key={stream.id}
                        onClick={() => setSelectedMediaItem(stream)}
                        style={{
                          flexShrink: 0,
                          width: 280,
                          backgroundColor: 'rgba(13, 17, 23, 0.75)',
                          backdropFilter: 'blur(16px)',
                          borderRadius: 10,
                          overflow: 'hidden',
                          border: stream.sessionState === 'live' ? '1px solid rgba(239, 68, 68, 0.45)' : '1px solid rgba(255, 255, 255, 0.08)',
                          boxShadow: '0 4px 14px rgba(0, 0, 0, 0.3)',
                          cursor: 'pointer',
                          display: 'flex',
                          flexDirection: 'column',
                          transition: 'transform 0.15s ease, border-color 0.15s ease',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.borderColor = stream.sessionState === 'live' ? '#ef4444' : '#38bdf8';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.borderColor = stream.sessionState === 'live' ? 'rgba(239, 68, 68, 0.45)' : 'rgba(255, 255, 255, 0.08)';
                        }}
                      >
                        {/* Poster with Live / Replay Badge */}
                        <div
                          style={{
                            position: 'relative',
                            width: '100%',
                            height: 140,
                            backgroundColor: '#090d16',
                            backgroundImage: `url(${stream.thumbnailUrl || (stream.type === 'whiteboard' ? 'https://storage.googleapis.com/gtv-videos-bucket/sample/images/ElephantsDream.jpg' : 'https://img.youtube.com/vi/jfKfPfyJRdk/maxresdefault.jpg')})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                          }}
                        >
                          <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.25)' }} />
                          <div style={{ position: 'absolute', top: 8, left: 8, display: 'flex', gap: 6, zIndex: 2 }}>
                            {stream.sessionState === 'live' ? (
                              <span
                                style={{
                                  padding: '3px 8px',
                                  borderRadius: 4,
                                  backgroundColor: '#ef4444',
                                  color: '#fff',
                                  fontSize: 10,
                                  fontWeight: 800,
                                  boxShadow: '0 0 8px rgba(239, 68, 68, 0.7)',
                                }}
                              >
                                🔴 LIVE NOW
                              </span>
                            ) : (
                              <span
                                style={{
                                  padding: '3px 8px',
                                  borderRadius: 4,
                                  backgroundColor: 'rgba(15, 23, 42, 0.85)',
                                  border: '1px solid rgba(255, 255, 255, 0.15)',
                                  color: '#94a3b8',
                                  fontSize: 10,
                                  fontWeight: 700,
                                }}
                              >
                                📼 REPLAY
                              </span>
                            )}
                            <span
                              style={{
                                padding: '3px 6px',
                                borderRadius: 4,
                                backgroundColor: 'rgba(0, 0, 0, 0.65)',
                                color: '#e2e8f0',
                                fontSize: 10,
                                fontWeight: 600,
                              }}
                            >
                              {stream.type === 'whiteboard' ? '🎨 Whiteboard' : stream.payload.video?.provider === 'youtube' ? '▶ YouTube' : '🎬 Vimeo'}
                            </span>
                          </div>

                          <div
                            style={{
                              position: 'absolute',
                              bottom: 8,
                              right: 8,
                              backgroundColor: 'rgba(0, 0, 0, 0.75)',
                              borderRadius: 4,
                              padding: '2px 6px',
                              color: '#fff',
                              fontSize: 11,
                              fontWeight: 600,
                            }}
                          >
                            {stream.stats.views.toLocaleString()} views
                          </div>
                        </div>

                        {/* Title & Metadata */}
                        <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
                          <h4
                            style={{
                              margin: 0,
                              color: '#f8fafc',
                              fontSize: 14,
                              fontWeight: 700,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {stream.title}
                          </h4>
                          <p
                            style={{
                              margin: 0,
                              color: '#94a3b8',
                              fontSize: 12,
                              lineHeight: 1.4,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {stream.description || 'Interactive sovereign session.'}
                          </p>
                          <div style={{ marginTop: 'auto', paddingTop: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 11 }}>
                            <span style={{ color: '#38bdf8', fontWeight: 600 }}>{stream.context?.channelName || stream.creatorHandle}</span>
                            <span style={{ color: '#64748b' }}>💬 {stream.stats.commentCount || 0} comments</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </ScrollableCardRow>
                )}
              </div>
            );
          }
          if (config.id === 'art_gallery_drops') {
            return (
              <div
                key="art_gallery_drops"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10,
                  backgroundColor: 'rgba(13, 17, 23, 0.5)',
                  backdropFilter: 'blur(16px)',
                  padding: '12px 14px',
                  borderRadius: 12,
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 2, marginRight: 2 }}>
                      <span style={{ color: '#64748b', fontSize: 13, cursor: 'grab' }} title="Drag / Reorder Shelf">
                        ⠿
                      </span>
                      <button
                        type="button"
                        onClick={() => handleMoveShelf('art_gallery_drops', 'UP')}
                        title="Move Shelf Up"
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: '#94a3b8',
                          cursor: 'pointer',
                          fontSize: 11,
                          padding: '2px 4px',
                        }}
                      >
                        ▲
                      </button>
                      <button
                        type="button"
                        onClick={() => handleMoveShelf('art_gallery_drops', 'DOWN')}
                        title="Move Shelf Down"
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: '#94a3b8',
                          cursor: 'pointer',
                          fontSize: 11,
                          padding: '2px 4px',
                        }}
                      >
                        ▼
                      </button>
                    </div>
                    <span style={{ fontSize: 18 }}>🛍️</span>
                    <span style={{ fontWeight: 800, fontSize: 16, color: '#f8fafc', letterSpacing: 0.2 }}>
                      {config.title}
                    </span>
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        background: 'rgba(16, 185, 129, 0.15)',
                        color: '#34d399',
                        border: '1px solid rgba(16, 185, 129, 0.3)',
                        padding: '2px 8px',
                        borderRadius: 6,
                      }}
                    >
                      Marketplace Drops
                    </span>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <button
                      type="button"
                      onClick={() => handleTogglePin('art_gallery_drops')}
                      style={{
                        background: config.isPinnedToTop ? 'rgba(56, 189, 248, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                        border: 'none',
                        borderRadius: 6,
                        padding: '4px 8px',
                        color: config.isPinnedToTop ? '#38bdf8' : '#94a3b8',
                        fontSize: 11,
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      📌 {config.isPinnedToTop ? 'Pinned' : 'Pin'}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleToggleCollapse('art_gallery_drops')}
                      style={{
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: 'none',
                        borderRadius: 6,
                        padding: '4px 8px',
                        color: '#94a3b8',
                        fontSize: 11,
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      {config.isCollapsed ? '▸ Expand' : '▾ Collapse'}
                    </button>
                    {!config.isCollapsed && (
                      <button
                        type="button"
                        onClick={onOpenGarage}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: '#38bdf8',
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                        }}
                      >
                        Explore Marketplace <ArrowRight size={14} />
                      </button>
                    )}
                  </div>
                </div>

                {!config.isCollapsed && (
                  <ScrollableCardRow gap={16}>
                    {INITIAL_GARAGE_DROPS.map((drop) => (
                      <GarageProductCard
                        key={drop.listingId}
                        product={drop}
                        onSelectProduct={(p) => {
                          onSelectProduct({
                            id: p.listingId,
                            channelId: p.channelId,
                            creatorId: p.creatorDid,
                            creatorHandle: p.sellerDisplayName || 'Creator',
                            title: p.title,
                            description: p.description,
                            price: { amount: p.priceUsd, currency: 'USD' },
                            inventoryType: p.fulfillmentType === 'DIGITAL_DOWNLOAD' ? 'digital_download' : p.fulfillmentType === 'SERVICE_CONSULTATION' ? 'service_consulting' : 'physical',
                            stockCount: p.stockRemaining,
                            previewMedia: {
                              thumbnailUrl: p.imageUrl || '🛍️',
                            },
                            escrowProtected: true,
                          });
                        }}
                        onBuyViaEscrow={(p) => {
                          onSelectProduct({
                            id: p.listingId,
                            channelId: p.channelId,
                            creatorId: p.creatorDid,
                            creatorHandle: p.sellerDisplayName || 'Creator',
                            title: p.title,
                            description: p.description,
                            price: { amount: p.priceUsd, currency: 'USD' },
                            inventoryType: p.fulfillmentType === 'DIGITAL_DOWNLOAD' ? 'digital_download' : p.fulfillmentType === 'SERVICE_CONSULTATION' ? 'service_consulting' : 'physical',
                            stockCount: p.stockRemaining,
                            previewMedia: {
                              thumbnailUrl: p.imageUrl || '🛍️',
                            },
                            escrowProtected: true,
                          });
                        }}
                      />
                    ))}
                  </ScrollableCardRow>
                )}
              </div>
            );
          }
          if (config.id === 'channels') {
            return (
              <MediaShelf
                key="channels"
                title={config.title}
                icon="🔥"
                items={trendingChannelsItems}
                isCollapsed={config.isCollapsed}
                isPinnedToTop={config.isPinnedToTop}
                onToggleCollapse={() => handleToggleCollapse('channels')}
                onTogglePin={() => handleTogglePin('channels')}
                onMoveUp={() => handleMoveShelf('channels', 'UP')}
                onMoveDown={() => handleMoveShelf('channels', 'DOWN')}
              />
            );
          }
          if (config.id === 'arcade_hall_of_fame') {
            return (
              <MediaShelf
                key="arcade_hall_of_fame"
                title={config.title}
                icon="🏆"
                items={hallOfFameItems}
                isCollapsed={config.isCollapsed}
                isPinnedToTop={config.isPinnedToTop}
                onToggleCollapse={() => handleToggleCollapse('arcade_hall_of_fame')}
                onTogglePin={() => handleTogglePin('arcade_hall_of_fame')}
                onMoveUp={() => handleMoveShelf('arcade_hall_of_fame', 'UP')}
                onMoveDown={() => handleMoveShelf('arcade_hall_of_fame', 'DOWN')}
              />
            );
          }
          return null;
        })}

      {/* ── Opt-In Contextual Attention Banner ────────────────────────────── */}
      <div style={{ marginTop: 8 }}>
        <ContextualAdBanner channelCategory="tech-depin" />
      </div>

      {/* ── Universal Media Composer Modal ─────────────────────────────────── */}
      {isComposerOpen && (
        <UniversalMediaComposerModal
          onClose={() => setIsComposerOpen(false)}
          onPublish={(newItem) => {
            setMediaStreams((prev) => [newItem, ...prev]);
          }}
        />
      )}

      {/* ── Selected Media Item Interactive Player & Discussion Modal ─────── */}
      {selectedMediaItem && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(10px)',
            zIndex: 9998,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: '840px',
              maxHeight: '90vh',
              overflowY: 'auto',
              position: 'relative',
              borderRadius: '20px',
              border: '1px solid rgba(255, 255, 255, 0.12)',
              boxShadow: '0 25px 60px rgba(0, 0, 0, 0.8)',
            }}
          >
            <button
              onClick={() => setSelectedMediaItem(null)}
              style={{
                position: 'absolute',
                top: 14,
                right: 14,
                zIndex: 20,
                width: 32,
                height: 32,
                borderRadius: '50%',
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                color: '#f8fafc',
                fontSize: 16,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              ✕
            </button>

            <UniversalMediaCard
              item={selectedMediaItem}
              onEnterChannel={(chanId) => {
                setSelectedMediaItem(null);
                onEnterChannel(chanId);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};
